/*
 * Cart drawer.
 *
 * CartDrawer / CartDrawerItems keep the same public surface Dawn expects (open, close,
 * renderContents, setActiveElement, getSectionsToRender) so product-form.js, quick-add and the
 * pubsub cart events keep working untouched. cart.js is not modified.
 *
 * Added on top:
 *  - cascading delete: removing a bundle line also removes the Kaching gift(s) linked to it
 *    (see data-gift-lines in snippets/cart-drawer-item.liquid)
 *  - stepping a quantity down to 0 removes the line, animation and gift cascade included
 *  - <cart-discount>: collapsible discount code field
 *  - render state is carried across section swaps (scroll position, discount panel, progress bar)
 *
 * Cart writes all go through /cart/change addressed by `line`. The Kaching Bundles app embed
 * wraps window.fetch and rewrites cart requests it recognises; keyed /cart/update calls came back
 * 422 and re-adds came back 400. `line` on /cart/change is the shape Dawn's own quantity stepper
 * uses, and the one call on this store the app reliably leaves alone.
 */

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // UI state that has to survive a section swap, since every cart mutation replaces
    // .drawer__inner wholesale.
    this.uiState = {
      scrollTop: 0,
      discountOpen: false,
      discountError: '',
      shippingPercent: null,
    };
    this.pendingSweep = false;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // scroll doesn't bubble, so capture it on the way down
    this.addEventListener(
      'scroll',
      (event) => {
        if (event.target instanceof HTMLElement && event.target.tagName === 'CART-DRAWER-ITEMS') {
          this.uiState.scrollTop = event.target.scrollTop;
        }
      },
      true
    );

    this.observeRenders();
    this.onRendered();
  }

  /*
   * Every cart mutation path ends in part of the drawer being replaced — .drawer__inner for
   * quantity and delete, all of #CartDrawer for renderContents. Watching this element (the only
   * one that is never itself replaced) catches them all, which beats hooking each update method.
   */
  observeRenders() {
    if (!window.MutationObserver) return;

    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        this.onRendered();
      });
    });

    observer.observe(this, { childList: true, subtree: true });
  }

  onRendered() {
    const items = this.querySelector('cart-drawer-items');
    if (items && this.uiState.scrollTop) {
      items.scrollTop = this.uiState.scrollTop;
    }

    this.restoreDiscountState();

    if (this.pendingSweep) {
      // the drawer is mid-open; hold the bar at zero so the open sweep is the only animation
      this.setShippingBar(0, { instant: true });
    } else {
      this.setShippingBar(this.shippingTarget(), { from: this.uiState.shippingPercent });
    }

    this.classList.remove('is-updating');
  }

  restoreDiscountState() {
    const discount = this.querySelector('cart-discount');
    // not upgraded yet on the very first pass, when there is no state to restore anyway
    if (!discount || typeof discount.setOpen !== 'function') return;

    if (this.uiState.discountOpen) discount.setOpen(true, false);

    if (this.uiState.discountError) {
      discount.showError(this.uiState.discountError);
      this.uiState.discountError = '';
    }
  }

  shippingTarget() {
    const bar = this.querySelector('.cart-drawer__shipping');
    return bar ? parseFloat(bar.dataset.percent) || 0 : null;
  }

  /*
   * Single entry point for the progress bar, so the open sweep and a cart update can never end up
   * animating it against each other — that fight was what made it stutter.
   *
   * The freshly rendered bar always carries its final value inline, so animating means putting it
   * back to `from`, flushing layout, then letting CSS transition it forward on the next frame.
   */
  setShippingBar(target, { from, instant } = {}) {
    const bar = this.querySelector('.cart-drawer__shipping');
    if (!bar || target === null) {
      this.uiState.shippingPercent = null;
      return;
    }

    this.uiState.shippingPercent = target;

    if (instant || from === undefined || from === null || from === target) {
      bar.classList.add('is-instant');
      bar.style.setProperty('--fs-percent', `${target}%`);
      void bar.offsetWidth;
      bar.classList.remove('is-instant');
      return;
    }

    bar.classList.add('is-instant');
    bar.style.setProperty('--fs-percent', `${from}%`);
    void bar.offsetWidth;
    bar.classList.remove('is-instant');

    requestAnimationFrame(() => {
      bar.style.setProperty('--fs-percent', `${target}%`);
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    /*
     * Sweep the progress bar up from zero, but only once the panel has finished sliding in.
     * Running both at once is what made the fill look jerky.
     */
    if (!this.classList.contains('active')) {
      this.pendingSweep = true;
      this.setShippingBar(0, { instant: true });
    }

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement);

        if (this.pendingSweep) {
          this.pendingSweep = false;
          this.setShippingBar(this.shippingTarget(), { from: 0 });
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    this.pendingSweep = false;
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      const removeButton = event.target.closest('.cart-item__remove');
      if (!removeButton) return;
      event.preventDefault();
      this.onRemoveClick(removeButton);
    });

    /*
     * Stepping the quantity down to 0 removes the line, the same as pressing the trash — so it
     * animates out and takes any attached gift with it. Captured on the way down so cart.js's own
     * change handler never sees it and fires a second, competing update.
     */
    this.addEventListener(
      'change',
      (event) => {
        const input = event.target.closest && event.target.closest('.quantity__input');
        if (!input || parseInt(input.value, 10) > 0) return;

        const row = input.closest('.cart-item');
        const removeButton = row && row.querySelector('.cart-item__remove');
        if (!removeButton) return;

        event.stopPropagation();
        this.onRemoveClick(removeButton);
      },
      true
    );
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  get drawer() {
    return this.closest('cart-drawer') || document.querySelector('cart-drawer');
  }

  /*
   * Replaces CartItems.connectedCallback so the drawer only refetches when it actually has to.
   *
   * cart.js's version re-fetches on every cart update, but the drawer is already being handed
   * fully rendered sections by whoever made the change: renderContents for a product form or
   * quick add, renderCartState for our own remove. Refetching on top of those was two wasted
   * network round trips on every add to cart.
   */
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items' || event.source === 'product-form') return;
      this.refresh();
    });
  }

  /*
   * One line change, addressed by line number.
   *
   * Everything here goes through /cart/change with `line` rather than /cart/update with line item
   * keys. The Kaching Bundles app embed wraps window.fetch and rewrites cart requests it
   * recognises; keyed /cart/update calls came back 422 and re-adds came back 400 once it had had
   * a go at them. `line` on /cart/change is the shape Dawn itself uses for the quantity stepper,
   * which is the one call in this cart the app has always left alone.
   */
  changeLine(line, quantity, withSections) {
    const body = { line: Number(line), quantity };
    if (withSections) {
      body.sections = this.getSectionsToRender().map((section) => section.section);
      body.sections_url = window.location.pathname;
    }

    return this.cartRequest(routes.cart_change_url, body);
  }

  /*
   * POST a cart payload and fail loudly. Shopify puts the useful part of a 4xx in the response
   * body, so it is read as text first and attached to the error — otherwise a rejected request
   * shows up as a bare "400 Bad Request" with no way to tell which field it objected to.
   */
  cartRequest(url, payload) {
    return fetch(url, { ...fetchConfig(), ...{ body: JSON.stringify(payload) } }).then((response) =>
      response.text().then((text) => {
        let data = {};
        try {
          data = JSON.parse(text);
        } catch (parseError) {
          data = {};
        }

        if (!response.ok || data.status) {
          const error = new Error(data.description || data.message || `${response.status} on ${url}`);
          error.detail = { url, payload, httpStatus: response.status, response: text.slice(0, 1000) };
          throw error;
        }

        return data;
      })
    );
  }

  /*
   * Remove several lines. Highest line number first — removing a line renumbers everything after
   * it, so descending order keeps the remaining targets valid without re-reading the cart.
   */
  removeLines(lines) {
    const ordered = [...new Set(lines.map(Number).filter((line) => line > 0))].sort((a, b) => b - a);
    if (!ordered.length) return Promise.resolve();

    const drawer = this.drawer;
    if (drawer) drawer.classList.add('is-updating');
    this.setBusy(true);

    const request = ordered.reduce(
      (chain, line, index) => chain.then(() => this.changeLine(line, 0, index === ordered.length - 1)),
      Promise.resolve()
    );

    /*
     * Hold the render until the row has finished collapsing. Without this the response often
     * lands first and the item vanishes mid-animation, which reads as a glitch rather than a
     * deliberate removal.
     */
    const settled = new Promise((resolve) => setTimeout(resolve, 260));

    return Promise.all([request, settled])
      .then(([state]) => {
        if (state) this.renderCartState(state);
      })
      .catch((error) => {
        console.error('[cart-drawer] remove failed', error.detail || error, error.message);
        this.showCartError((error && error.message) || window.cartStrings.error);
        return this.refresh();
      })
      .finally(() => {
        this.setBusy(false);
        if (drawer) drawer.classList.remove('is-updating');
      });
  }

  /* Deleting a bundle takes its gift(s) with it, so no orphaned $0 line is left behind. */
  onRemoveClick(button) {
    const lines = [button.dataset.line];
    const giftLines = (button.dataset.giftLines || '').split(',').filter(Boolean);
    lines.push(...giftLines);

    const group = button.closest('.cart-line-group');
    const row = button.closest('.cart-item');

    // collapse the whole group when the bundle goes, just the row when only the gift goes
    if (giftLines.length || !row.classList.contains('cart-item--gift')) {
      if (group) group.classList.add('is-removing');
    } else {
      row.classList.add('is-removing');
    }

    this.removeLines(lines);
  }

  /*
   * Re-queried rather than captured: a render swaps the element out, so a reference taken before
   * the request would leave the class stuck on a detached node and the live list greyed out.
   */
  setBusy(busy) {
    const cartItems = document.getElementById('CartDrawer-CartItems');
    if (cartItems) cartItems.classList.toggle('cart__items--disabled', busy);
  }

  /*
   * Re-render the drawer from the current cart without changing anything. Plain section GETs —
   * no cart mutation, so nothing here can be rejected or rewritten by an app interceptor.
   */
  refresh() {
    // both sections in flight at once rather than one after the other
    return Promise.all([
      fetch(`${routes.cart_url}?section_id=cart-drawer`).then((response) => response.text()),
      fetch(`${routes.cart_url}?section_id=cart-icon-bubble`).then((response) => response.text()),
    ])
      .then(([drawerHTML, bubbleHTML]) => {
        const parser = new DOMParser();

        const html = parser.parseFromString(drawerHTML, 'text/html');
        const source = html.querySelector('.drawer__inner');
        const target = document.querySelector('#CartDrawer .drawer__inner');

        if (source && target) {
          const sourceDrawer = html.querySelector('cart-drawer');
          const drawer = document.querySelector('cart-drawer');
          if (drawer && sourceDrawer) drawer.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
          target.innerHTML = source.innerHTML;
        }

        const bubble = document.getElementById('cart-icon-bubble');
        const bubbleSource = parser.parseFromString(bubbleHTML, 'text/html').querySelector('.shopify-section');
        if (bubble && bubbleSource) bubble.innerHTML = bubbleSource.innerHTML;
      })
      .catch((error) => console.error('[cart-drawer] refresh failed', error));
  }

  renderCartState(parsedState) {
    const cartDrawerWrapper = document.querySelector('cart-drawer');

    if (typeof parsedState.item_count === 'number') {
      this.classList.toggle('is-empty', parsedState.item_count === 0);
      if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    this.getSectionsToRender().forEach((section) => {
      const container = document.getElementById(section.id);
      if (!container || !parsedState.sections || !parsedState.sections[section.section]) return;
      const elementToReplace = container.querySelector(section.selector) || container;
      elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
    });

    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState });
  }

  showCartError(message) {
    const errors = document.getElementById('CartDrawer-CartErrors');
    if (errors) errors.textContent = message;

    document.querySelectorAll('.cart-line-group.is-removing, .cart-item.is-removing').forEach((element) => {
      element.classList.remove('is-removing');
    });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDiscount extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;

    this.toggle = this.querySelector('.cart-discount__toggle');
    this.input = this.querySelector('.cart-discount__input');
    this.applyButton = this.querySelector('.cart-discount__apply');
    this.errorElement = this.querySelector('.cart-discount__error');

    this.toggle.addEventListener('click', () => this.setOpen(!this.classList.contains('is-open')));
    this.applyButton.addEventListener('click', () => this.apply());
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.apply();
      }
    });
  }

  get drawer() {
    return this.closest('cart-drawer') || document.querySelector('cart-drawer');
  }

  setOpen(open, remember = true) {
    this.classList.toggle('is-open', open);
    this.toggle.setAttribute('aria-expanded', String(open));

    const drawer = this.drawer;
    if (remember && drawer) drawer.uiState.discountOpen = open;
  }

  showError(message) {
    if (!this.errorElement) return;
    this.errorElement.textContent = message;
    this.errorElement.hidden = !message;
  }

  /*
   * The AJAX cart cannot apply a discount code directly. /discount/CODE stores it on the session
   * (which is what checkout reads) and Shopify reflects it in cart.discount_applications, so the
   * drawer is re-rendered afterwards and the result is verified against what actually landed.
   */
  apply() {
    const code = this.input.value.trim();
    if (!code) return;

    const cartItems = document.querySelector('cart-drawer-items');
    if (!cartItems) return;

    const drawer = this.drawer;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    this.showError('');
    this.applyButton.setAttribute('aria-busy', 'true');

    const discountUrl = `${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(`${root}cart.js`)}`;

    fetch(discountUrl, { method: 'GET', credentials: 'same-origin' })
      .then(() => cartItems.refresh())
      .then(() => {
        const applied = document.querySelector('cart-discount');
        const codes = ((applied && applied.dataset.appliedCodes) || '')
          .split(',')
          .map((value) => value.trim().toLowerCase())
          .filter(Boolean);

        if (!codes.includes(code.toLowerCase())) {
          if (drawer) drawer.uiState.discountError = `“${code}” isn’t a valid discount code for this cart.`;
          if (applied) applied.showError(`“${code}” isn’t a valid discount code for this cart.`);
        }
      })
      .catch(() => {
        this.showError(window.cartStrings.error);
      })
      .finally(() => {
        this.applyButton.removeAttribute('aria-busy');
      });
  }
}

customElements.define('cart-discount', CartDiscount);
